<template>
  <div class="states-table table-component p-2">
    <artisio-table :index="false" selectable ref="statesTable" :pagination="tableData.pagination"
                   :sort="tableData.ordering" :items="tableData.rows" :busy="tableData.loading"
                   :checkboxes="true" :search="tableData.search" :enable-simple-search="false"
                   :fields="stateTableFields" filter-toggle-string="states-table" :extra-data="extraData"
                   @paramsChanged="getTableData" @onSortChange="removeExpandedTables">
      <template v-slot:header-buttons="{selectedItems}">
        <b-dropdown variant="primary" right :text="$t('Actions')">
          <!--
          <b-dropdown-item @click="onNewStateClick()">
            {{ $t('Create New State') }}
          </b-dropdown-item>
          -->
          <b-dropdown-item @click="onNewCountyClick()">
            {{ $t('Create New County') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onNewCityClick()">
            {{ $t('Create New City') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onChangeThresholdStatus(selectedItems)"
                           :disabled="disableThresholdStatusMenuItem(selectedItems)">
            {{ $t('Mark Threshold Reached') }}
          </b-dropdown-item>
          <b-dropdown-item @click="onChangeThresholdStatus(selectedItems, false)"
                           :disabled="disableThresholdStatusMenuItem(selectedItems)">
            {{ $t('Un-mark Threshold Reached') }}
          </b-dropdown-item>
        </b-dropdown>
      </template>
      <template v-slot:search-form-placeholder>
        <search-collapse table-id="states-table">
          <state-search :exclude-search-fields="excludeSearchFields"/>
        </search-collapse>
      </template>
      <template slot="cell(name)" slot-scope="{item: state, detailsShowing, toggleDetails}">
        <a v-if="state.num_counties > 0" href="javascript:void(0)"
           @click="onStateViewDetailsCLick(state.uuid, detailsShowing, toggleDetails)">
          {{ state.name }}
        </a>
        <span v-else>{{ state.name }}</span>
      </template>
      <template slot="cell(tax_rate)" slot-scope="{item: state}">
        <span v-if="state.tax_rate !== null">{{ state.tax_rate }}</span>
        <span v-else v-as-status="'N/A'">{{ $t('N/A') }}</span>
      </template>
      <template slot="cell(exclude_tax_for_premium)" slot-scope="{item: state}">
        <span v-html="$options.filters.asBadgeBoolean(state.exclude_tax_for_premium)"/>
      </template>
      <template slot="cell(is_economic_nexus_threshold_reached)" slot-scope="{item: state}">
        <span v-html="$options.filters.asBadgeBoolean(state.is_economic_nexus_threshold_reached)"/>
      </template>
      <template slot="cell(actions)" slot-scope="{item: state, detailsShowing, toggleDetails}">
        <sales-tax-action-buttons :exclude-actions="[SALES_TAX_DELETE_ACTION]"
                                  :object="state" type="State" child-name="County"
                                  :disable-actions="disableActions(state.num_counties)"
                                  @onAddClick="onNewCountyClick" @onEditClick="onStateEditClick"
                                  @onDeleteClick="onStateDeleteClick"
                                  @onViewDetailsClick="onStateViewDetailsCLick($event, detailsShowing, toggleDetails)">
        </sales-tax-action-buttons>
      </template>
      <template v-slot:row-details="{item: state}">
        <b-table class="county-table" :items="state.counties || []" :fields="countyTableFields"
                 :busy="state.loading">
          <template slot="cell(name)" slot-scope="{item: county, detailsShowing, toggleDetails}">
            <a v-if="county.num_cities > 0" href="javascript:void(0)"
               @click="onCountyViewDetailsCLick(county.uuid, state.uuid, detailsShowing, toggleDetails)">
              {{ county.name }}
            </a>
            <span v-else>{{ county.name }}</span>
          </template>
          <template slot="cell(tax_rate)" slot-scope="{item: county}">
            <span v-if="county.tax_rate !== null">{{ county.tax_rate }}</span>
            <span v-else v-as-status="'N/A'">{{ $t('N/A') }}</span>
          </template>
          <template slot="cell(exclude_tax_for_premium)" slot-scope="{item: county}">
            <span v-html="$options.filters.asBadgeBoolean(county.exclude_tax_for_premium)"/>
          </template>
          <template slot="cell(actions)" slot-scope="{item: county, detailsShowing, toggleDetails}">
            <sales-tax-action-buttons
              :object="county" type="County" child-name="City" :disable-actions="disableActions(county.num_cities)"
              @onAddClick="onNewCityClick" @onEditClick="onCountyEditClick" @onDeleteClick="onCountyDeleteClick"
              @onViewDetailsClick="onCountyViewDetailsCLick($event, state.uuid, detailsShowing, toggleDetails)">
            </sales-tax-action-buttons>
          </template>
          <template v-slot:row-details="{item: county}">
            <b-table class="city-table" :index="false" :items="county.cities || []" :enable-simple-search="false"
                     :fields="cityTableFields" :busy="county.loading">
              <template slot="cell(tax_rate)" slot-scope="{item: city}">
                <span v-if="city.tax_rate !== null">{{ city.tax_rate }}</span>
                <span v-else v-as-status="'N/A'">{{ $t('N/A') }}</span>
              </template>
              <template slot="cell(exclude_tax_for_premium)" slot-scope="{item: city}">
                <span v-html="$options.filters.asBadgeBoolean(city.exclude_tax_for_premium)"/>
              </template>
              <template slot="cell(actions)" slot-scope="{item: city}">
                <sales-tax-action-buttons
                  :object="city" type="City" :exclude-actions="[SALES_TAX_VIEW_DETAILS_ACTION, SALES_TAX_ADD_ACTION]"
                  @onEditClick="onCityEditClick" @onDeleteClick="onCityDeleteClick">
                </sales-tax-action-buttons>
              </template>
            </b-table>
          </template>
        </b-table>
      </template>
    </artisio-table>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import ArtisioTable from "../../../core/components/ams-table/ArtisioTable";
  import i18n from "../../../shared/i18n";
  import SearchCollapse from "../../../core/components/search-collapse/SearchCollapse";
  import {
    SALES_TAX_ADD_ACTION,
    SALES_TAX_DELETE_ACTION,
    SALES_TAX_EDIT_ACTION,
    SALES_TAX_VIEW_DETAILS_ACTION,
    STATES_TABLE_NAME
  } from "../../constants";
  import StateSearch from "./StateSearch";
  import SalesTaxActionButtons from "./components/SalesTaxActionButtons";

  const {mapState, mapActions} = createNamespacedHelpers('setup/salesTaxConfiguration');
  export default {
    name: "StatesTable",
    components: {SalesTaxActionButtons, StateSearch, SearchCollapse, ArtisioTable},
    props: {
      excludeSearchFields: {
        type: Array,
        default: () => []
      },
    },
    data() {
      return {
        stateTableFields: [
          {key: "name", label: i18n.t("Name"), thClass: 'name-col', sortable: true},
          {key: "country_code", label: i18n.t("Country Code"), thClass: 'country-code-col', sortable: true},
          {key: "alpha_code", label: i18n.t("Alpha Code"), thClass: 'alpha-code-col', sortable: true},
          {key: "tax_rate", label: i18n.t("Tax Rate"), thClass: 'tax-rate-col', sortable: true},
          {
            key: "exclude_tax_for_premium",
            label: i18n.t("Exc. Tax For Premium"),
            thClass: 'exclude-tax-for-premium-col',
            sortable: true
          },
          {
            key: "is_economic_nexus_threshold_reached",
            label: i18n.t("Is Economic Nexus Threshold Reached"),
            thClass: 'is-economic-nexus-threshold-reached-col',
            sortable: true
          },
          {key: 'actions', label: i18n.t('Actions')},
        ],
        countyTableFields: [
          {key: "name", label: i18n.t("Name")},
          {key: "tax_rate", label: i18n.t("Tax Rate")},
          {key: "exclude_tax_for_premium", label: i18n.t("Exc. Tax For Premium")},
          {key: "actions", label: i18n.t("Actions")},
        ],
        cityTableFields: [
          {key: "name", label: i18n.t("Name")},
          {key: "tax_rate", label: i18n.t("Tax Rate")},
          {key: "exclude_tax_for_premium", label: i18n.t("Exc. Tax For Premium")},
          {key: "actions", label: i18n.t("Actions")},
        ],
        SALES_TAX_VIEW_DETAILS_ACTION: SALES_TAX_VIEW_DETAILS_ACTION,
        SALES_TAX_EDIT_ACTION: SALES_TAX_EDIT_ACTION,
        SALES_TAX_ADD_ACTION: SALES_TAX_ADD_ACTION,
        SALES_TAX_DELETE_ACTION: SALES_TAX_DELETE_ACTION,
      }
    },
    computed: {
      ...mapState({
        rows: state => state.state.tableData.rows,
        tableData: state => state.state.tableData,
      }),
      extraData() {
        return {
          tableName: STATES_TABLE_NAME,
        }
      },
    },
    methods: {
      ...mapActions(['getTableData', 'showStateModal', 'deleteState', 'showCountyModal', 'deleteCounty',
        'showCityModal', 'deleteCity', 'changeThresholdStatus', 'getCountiesByState', 'getCitiesByCounty',
        'addExpandedState', 'removeExpandedState', 'addExpandedCounty', 'removeExpandedCounty', 'removeExpandedTables']),
      onNewStateClick() {
        this.showStateModal(null);
      },
      onStateEditClick(item) {
        this.showStateModal(item);
      },
      async onStateViewDetailsCLick(id, detailsShowing, fn) {
        fn();
        if (!detailsShowing) {
          this.addExpandedState(id);
          await this.getCountiesByState({load_all: true, state_uuid: id});
        } else {
          this.removeExpandedState(id);
        }
      },
      async onStateDeleteClick(item) {
        this.$confirm(
          this.$i18n.t(`Are you sure you want to delete following state?`),
          this.$i18n.t(`State was successfully deleted`),
          this.deleteState,
          item
        );
      },
      onNewCountyClick($event) {
        let params = null;
        if ($event) {
          params = {state_uuid: $event.uuid};
        }
        this.showCountyModal(params);
      },
      onCountyEditClick(item) {
        this.showCountyModal(item);
      },
      async onCountyDeleteClick(item) {
        this.$confirm(
          this.$i18n.t(`Are you sure you want to delete following county?`),
          this.$i18n.t(`County was successfully deleted`),
          this.deleteCounty,
          item
        );
      },
      async onCountyViewDetailsCLick(id, stateUuid, detailsShowing, fn) {
        fn();
        if (!detailsShowing) {
          this.addExpandedCounty(id);
          await this.getCitiesByCounty({load_all: true, county_uuid: id, state_uuid: stateUuid});
        } else {
          this.removeExpandedCounty({countyUuid: id, stateUuid: stateUuid});
        }
      },
      onNewCityClick($event) {
        let params = null;
        if ($event) {
          params = {county: $event, county_uuid: $event.uuid};
        }
        this.showCityModal(params);
      },
      onCityEditClick(item) {
        this.showCityModal(item);
      },
      onCityDeleteClick(item) {
        this.$confirm(
          this.$i18n.t(`Are you sure you want to delete following city?`),
          this.$i18n.t(`City was successfully deleted`),
          this.deleteCity,
          item
        );
      },
      disableActions(numOfChildItems) {
        return numOfChildItems === 0 ? [this.SALES_TAX_VIEW_DETAILS_ACTION] : [];
      },
      async onChangeThresholdStatus(selectedItems, thresholdReached = true) {
        let stateUuids = []
        selectedItems.forEach(item => {
          stateUuids.push(item.uuid);
        })
        const params = {
          threshold_reached: thresholdReached,
          state_uuids: stateUuids,
        }
        this.$confirm(
          this.$i18n.t(`Are you sure you want to change threshold status?`),
          this.$i18n.t(`Threshold status changed successfully`),
          this.changeThresholdStatus,
          params,
        );
      },
      disableThresholdStatusMenuItem(selectedItems) {
        return !selectedItems || selectedItems.size < 1;
      },
    },
    mounted() {
      this.getTableData();
    },
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .artisio-table {
    /deep/ {


      tr {
        > td:first-child {
          border-left: 5px solid transparent;
        }
      }

      .b-table-has-details {
        > td:first-child {
          border-left: 5px solid $primary
        }
      }

      .b-table-details {
        > td:first-child {
          border-left: 5px solid $primary;
          padding: 0px !important;
        }
      }

      .b-table {
        > thead > tr > th {
          .custom-control {
            padding-left: 2.02em;
          }
        }

        tbody tr td {
          padding: 5px;
        }
      }

      .table {
        margin-bottom: 0;
      }

      .name-col {
        min-width: 125px;
      }

      .country-code-col {
        min-width: 100px;
      }

      .alpha-code-col {
        min-width: 100px;
      }

      .tax-rate-col {
        min-width: 100px;
      }

      .exclude-tax-for-premium-col {
        min-width: 100px;
      }

      .is-economic-nexus-threshold-reached-col {
        min-width: 100px;
      }
    }
  }

  .city-table {
    margin-left: 7px;
  }

  .county-table {
    margin-left: 7px;

    /deep/ {
      .table {
        margin-bottom: 0;
      }

      thead tr th {
        background: unset;
        border-right: unset;
        border-left: unset;
        color: unset;
        font-weight: bold;
        padding: 4px;
      }

      tbody tr td {
        padding: 2px;
      }

      .b-table-has-details {
        > td:first-child {
          border-left: 5px solid $success;
        }
      }

      .b-table-details {
        > td:first-child {
          border-left: 5px solid $success;
        }
      }
    }
  }
</style>